import { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Spinner, Table } from "react-bootstrap";
import { TopographNavbar } from "../components/TopographNavbar";
import { useAuth } from "../context/AuthContext";
import { CLIENT } from "../lib/api";
import { fulfillmentBadge, paymentBadge } from "../lib/status";
import type { Order } from "../lib/types";

const dollarFmt = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
});
const dollars = (cents: number) => dollarFmt.format(cents / 100);
const when = (unixSeconds: number) =>
    unixSeconds ? new Date(unixSeconds * 1000).toLocaleString() : "—";

export function Orders() {
    const auth = useAuth();
    const [orders, setOrders] = useState<Order[] | null>(null);
    const [mapNames, setMapNames] = useState<Record<string, string>>({});
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!auth.email) return;
        try {
            const [ordersResp, mapsResp] = await Promise.all([
                CLIENT.listOrders(),
                CLIENT.listMaps(auth.email),
            ]);
            setOrders(ordersResp.orders);
            setMapNames(
                Object.fromEntries(mapsResp.maps.map((m) => [m.id, m.friendlyName])),
            );
            setError(null);
        } catch (err) {
            setError(`Failed to load orders: ${err}`);
        }
    }, [auth.email]);

    useEffect(() => {
        if (!auth.loggedIn) return;
        load();
        const id = window.setInterval(load, 15_000);
        return () => window.clearInterval(id);
    }, [auth.loggedIn, load]);

    const cancel = async (o: Order) => {
        try {
            const updated = await CLIENT.cancelOrder(o.id);
            setOrders((prev) => prev?.map((x) => (x.id === o.id ? updated : x)) ?? prev);
        } catch (err) {
            setError(`Cancel failed: ${err}`);
        }
    };

    if (!auth.loggedIn) {
        return (
            <>
                <TopographNavbar />
                <br />
                <div style={{ marginLeft: "1rem" }}>You are not logged in! Log in to see your orders.</div>
            </>
        );
    }

    return (
        <>
            <TopographNavbar />
            <br />
            <div style={{ margin: "0 1rem" }}>
                <h5>
                    <i className="bi bi-bag-check" /> Your Orders
                </h5>
                {error && <Alert variant="danger">{error}</Alert>}
                {orders === null ? (
                    <div><Spinner size="sm" /> Loading orders…</div>
                ) : orders.length === 0 ? (
                    <div style={{ color: "#999" }}>
                        No orders yet — pick a rendered map on the{" "}
                        <a href="/renders">Renders</a> page and hit Checkout.
                    </div>
                ) : (
                    <Table hover responsive variant="dark" size="sm" style={{ verticalAlign: "middle" }}>
                        <thead>
                            <tr>
                                <th>Created</th>
                                <th>Map</th>
                                <th>Total</th>
                                <th>Payment</th>
                                <th>Fulfillment</th>
                                <th>Tracking</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((o) => {
                                const pb = paymentBadge(o.paymentStatus);
                                const fb = fulfillmentBadge(o.status);
                                const payable =
                                    (o.paymentStatus === "requires_payment" ||
                                        o.paymentStatus === "failed") &&
                                    o.status !== "canceled";
                                const cancellable =
                                    o.status !== "canceled" &&
                                    o.status !== "refunded" &&
                                    o.paymentStatus !== "succeeded" &&
                                    o.paymentStatus !== "partially_refunded" &&
                                    o.paymentStatus !== "refunded";
                                return (
                                    <tr key={o.id}>
                                        <td>{when(o.createTime)}</td>
                                        <td title={o.mapId}>
                                            {mapNames[o.mapId] || <code>{o.mapId.slice(0, 8)}</code>}
                                        </td>
                                        <td>
                                            {dollars(o.amountCents)}
                                            {(o.discountCents ?? 0) > 0 && (
                                                <div style={{ color: "#999", fontSize: "0.8em" }}>
                                                    coupon <code>{o.couponCode}</code> −{dollars(o.discountCents!)}
                                                </div>
                                            )}
                                        </td>
                                        <td><Badge bg={pb.variant}>{pb.label}</Badge></td>
                                        <td><Badge bg={fb.variant}>{fb.label}</Badge></td>
                                        <td>{o.shippingId || <span style={{ color: "#666" }}>—</span>}</td>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            {payable && (
                                                <Button
                                                    size="sm"
                                                    variant="warning"
                                                    href={`/checkout?mapId=${encodeURIComponent(o.mapId)}&orderId=${encodeURIComponent(o.id)}`}
                                                >
                                                    <i className="bi bi-credit-card" /> Pay now
                                                </Button>
                                            )}{" "}
                                            {cancellable && (
                                                <Button
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    onClick={() => cancel(o)}
                                                >
                                                    Cancel
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                )}
            </div>
        </>
    );
}
